import { Fragment } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import {
  formatMarketingCaseNumber,
  sumMarketingCaseEstimate,
  toMarketingCaseImageUrl,
  type MarketingCaseDisplay,
  type MarketingCaseEstimateGroup,
} from '@/utils/approval/marketingCase';

/** 조회·작성 화면이 이 id 로 버튼은 포탈 버튼, 모달 틀은 포탈 모달로 바꿔 그림 */
export const DETAIL_BUTTON_CLASS = 'mkt-case-detail-button';
export const DETAIL_MODAL_ID = 'mktCaseDetailModal';
export const DETAIL_TITLE_ID = 'mktCaseDetailTitle';
export const DETAIL_CONTENT_ID = 'mktCaseDetailContent';

const TABLE_CLASS = 'table table-bordered';

const MultiLine = ({ value }: { value: string | null | undefined }) => (
  <>
    {(value ?? '').split(/\r?\n/).map((line, i) => (
      <Fragment key={i}>
        {i > 0 && <br />}
        {line}
      </Fragment>
    ))}
  </>
);

type Props = { display: MarketingCaseDisplay; brandName: string; t: TFunction };

/** 레거시 appr_sub_view_forms_mkt_deatil_view — 브랜드별 명세·소계, 맨 아래 Grand Total */
const EstimateTable = ({
  groups,
  t,
}: {
  groups: MarketingCaseEstimateGroup[];
  t: TFunction;
}) => {
  const num = formatMarketingCaseNumber;
  return (
    <table className={TABLE_CLASS}>
      <colgroup>
        {[10, 9, 20, 12, 9, 5, 9, 9, 9, 9].map((w, i) => (
          <col key={i} width={`${w}%`} />
        ))}
      </colgroup>
      <tbody>
        <tr>
          <th className="mkt-case-caption" colSpan={10}>
            {t('예상금액 상세내역')}
          </th>
        </tr>
        <tr>
          <th>{t('브랜드')}</th>
          <th>{t('중구분')}</th>
          <th>{t('소구분')}</th>
          <th>{t('항목')}</th>
          <th>{t('단가')}</th>
          <th>{t('수량')}</th>
          <th>{t('총액')}</th>
          <th>{t('금액구분')}</th>
          <th>{t('지급처코드')}</th>
          <th>{t('지급처 명')}</th>
        </tr>
        {groups.map((group, gi) => {
          const items = group.items ?? [];
          const brandCell = (
            <td
              className="text-center mkt-case-brand"
              rowSpan={items.length + 1}
            >
              {group.brandName}
            </td>
          );
          return (
            <Fragment key={gi}>
              {items.map((item, ii) => (
                <tr key={ii}>
                  {ii === 0 && brandCell}
                  <td>{item.categoryName}</td>
                  <td>{item.subCategoryName}</td>
                  <td>{item.description}</td>
                  <td className="text-right">{num(item.price)}</td>
                  <td className="text-right">{num(item.quantity)}</td>
                  <td className="text-right">{num(item.amount)}</td>
                  <td className="text-center">{item.paymentTypeName}</td>
                  <td className="text-center">{item.vendorCode}</td>
                  <td className="text-center">
                    {item.vendorCode === 'P' ? t('개인') : item.vendorName}
                  </td>
                </tr>
              ))}
              <tr className="mkt-case-subtotal">
                {items.length === 0 && brandCell}
                <td>{t('소계')}</td>
                <td />
                <td />
                <td className="text-right">{num(group.subtotalPrice)}</td>
                <td />
                <td className="text-right">{num(group.subtotalAmount)}</td>
                <td />
                <td />
                <td />
              </tr>
            </Fragment>
          );
        })}
        <tr className="mkt-case-total">
          <td className="text-center" colSpan={4}>
            Grand Total
          </td>
          <td />
          <td />
          <td className="text-right">
            {num(sumMarketingCaseEstimate(groups))}
          </td>
          <td colSpan={3} />
        </tr>
      </tbody>
    </table>
  );
};

/** 레거시 appr_sub_view_forms_mkt_by_case — 머리 정보·진행 상세·진행비용 */
const MarketingCaseTables = ({ display, brandName, t }: Props) => {
  const { header, cost } = display;
  const details = display.progressDetails ?? [];
  const imageUrl = toMarketingCaseImageUrl(header?.imageUrl);
  const num = formatMarketingCaseNumber;
  return (
    <>
      {header && (
        <table className={TABLE_CLASS}>
          <colgroup>
            <col width="15%" />
            <col width="85%" />
          </colgroup>
          <tbody>
            <tr>
              <th className="mkt-case-caption" colSpan={2}>
                {t('{{brand}} 마케팅 진행의 건', {
                  brand: brandName,
                  interpolation: { escapeValue: false },
                })}
              </th>
            </tr>
            <tr>
              <th>{t('진행업무')}</th>
              <td>{header.title}</td>
            </tr>
            <tr>
              <th>{t('진행브랜드')}</th>
              <td>
                {(header.brands ?? []).map((b, i) => (
                  <Fragment key={b.brandCode ?? i}>
                    {i > 0 && '\u00a0 '}
                    <span
                      className={`mkt-case-brand-item${
                        b.isParticipating ? ' is-on' : ''
                      }`}
                    >
                      <span className="mkt-case-check">
                        {b.isParticipating ? '■' : '□'}
                      </span>{' '}
                      {b.brandName}
                    </span>
                  </Fragment>
                ))}
              </td>
            </tr>
            <tr>
              <th>{t('목적')}</th>
              <td>
                <MultiLine value={header.purpose} />
              </td>
            </tr>
            <tr>
              <th>{t('예산 시즌')}</th>
              <td>{header.season}</td>
            </tr>
            <tr>
              <th>{t('기간')}</th>
              <td>{`${header.termStart ?? ''}~${header.termEnd ?? ''}`}</td>
            </tr>
          </tbody>
        </table>
      )}
      {details.length > 0 && (
        <table className={TABLE_CLASS}>
          <colgroup>
            <col width="15%" />
            <col width="85%" />
          </colgroup>
          <tbody>
            <tr>
              <th className="mkt-case-caption" colSpan={2}>
                {t('진행 상세')}
              </th>
            </tr>
            {details.map((row, i) => (
              <tr key={i}>
                <th>{row.title}</th>
                <td>
                  <MultiLine value={row.detail} />
                </td>
              </tr>
            ))}
            {imageUrl && (
              <tr>
                <th>{t('이미지')}</th>
                <td className="text-center">
                  <img className="mkt-case-image" src={imageUrl} alt="" />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      {cost && (
        <table className={TABLE_CLASS}>
          <colgroup>
            {[0, 1, 2, 3].map((i) => (
              <col key={i} width="25%" />
            ))}
          </colgroup>
          <tbody>
            <tr>
              <th className="mkt-case-caption" colSpan={4}>
                {t('진행비용')}
              </th>
            </tr>
            <tr>
              <th>{t('업체제안가')}</th>
              <th>{t('네고견적')}</th>
              <th>{t('네고율')}</th>
              <td className="text-center" rowSpan={2}>
                <button type="button" className={DETAIL_BUTTON_CLASS}>
                  {t('상세보기')}
                </button>
              </td>
            </tr>
            <tr>
              <td className="text-right">{num(cost.offerAmount)}</td>
              <td className="text-right">{num(cost.negotiatedAmount)}</td>
              <td className="text-right">{`${num(
                cost.negotiationRate,
                2,
              )}%`}</td>
            </tr>
          </tbody>
        </table>
      )}
      <div id={DETAIL_MODAL_ID} style={{ display: 'none' }}>
        <h4 id={DETAIL_TITLE_ID}>{t('견적 상세내역')}</h4>
        <div id={DETAIL_CONTENT_ID}>
          <EstimateTable groups={display.estimate ?? []} t={t} />
        </div>
      </div>
    </>
  );
};

/** attributes.content — 레거시는 상세보기를 열 때마다 지금 값을 조회했지만, 결재된 내용이 바뀌지 않게 작성 시점 견적 상세까지 저장 */
export const buildMarketingCaseContentHtml = (
  display: MarketingCaseDisplay,
  brandName: string,
  t: TFunction,
): string =>
  renderToStaticMarkup(
    <div className="mkt-case">
      <MarketingCaseTables display={display} brandName={brandName} t={t} />
    </div>,
  );
